import type { ReactNode } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { useTranslation } from '../i18n';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { UserIcon } from '../components/icons';
import { displayNameOf, formatDateTime, initialsOf } from '../lib/utils';

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-4 border-b border-border py-3 last:border-b-0">
      <span className="text-sm text-muted">{label}</span>
      <span className="text-sm font-medium text-fg">{children}</span>
    </div>
  );
}

export default function ProfilePage() {
  const { user } = useAuth();
  const { t, locale } = useTranslation();

  if (!user) return null;

  const name = displayNameOf(user);

  return (
    <div className="mx-auto max-w-4xl px-4 py-10">
      <h1 className="text-2xl font-semibold tracking-tight text-fg">{t('profile.title')}</h1>
      <p className="mt-1 text-sm text-muted">{t('profile.subtitle')}</p>

      <div className="mt-8 grid gap-6 md:grid-cols-3">
        <Card className="md:col-span-1">
          <CardContent className="flex flex-col items-center pt-8 text-center">
            {user.profile?.avatarUrl ? (
              <img
                src={user.profile.avatarUrl}
                alt=""
                className="h-20 w-20 rounded-full object-cover"
              />
            ) : (
              <div
                className="flex h-20 w-20 items-center justify-center rounded-full bg-brand text-2xl font-semibold text-brand-fg"
                aria-hidden="true"
              >
                {initialsOf(user.email)}
              </div>
            )}
            <h2 className="mt-4 text-lg font-semibold text-fg">{name}</h2>
            <p className="break-all text-sm text-muted">{user.email}</p>
            <div className="mt-3 flex flex-wrap justify-center gap-2">
              <Badge tone={user.role === 'admin' ? 'brand' : 'neutral'}>
                {t(user.role === 'admin' ? 'role.admin' : 'role.user')}
              </Badge>
              <Badge tone={user.status === 'active' ? 'success' : 'warning'}>
                {t(`status.${user.status}` as const)}
              </Badge>
            </div>
          </CardContent>
        </Card>

        <div className="space-y-6 md:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <UserIcon className="h-4 w-4 text-muted" />
                {t('profile.details.title')}
              </CardTitle>
              <CardDescription>{t('profile.details.description')}</CardDescription>
            </CardHeader>
            <CardContent>
              <Row label={t('profile.field.displayName')}>
                {user.profile?.displayName ?? '\u2014'}
              </Row>
              <Row label={t('profile.field.country')}>{user.profile?.country ?? '\u2014'}</Row>
              <Row label={t('profile.field.bio')}>{user.profile?.bio ?? '\u2014'}</Row>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>{t('profile.account.title')}</CardTitle>
              <CardDescription>{t('profile.account.description')}</CardDescription>
            </CardHeader>
            <CardContent>
              <Row label={t('profile.field.email')}>{user.email}</Row>
              <Row label={t('profile.field.emailVerified')}>
                {user.emailVerified ? t('common.yes') : t('common.no')}
              </Row>
              <Row label={t('profile.field.role')}>
                {t(user.role === 'admin' ? 'role.admin' : 'role.user')}
              </Row>
              <Row label={t('profile.field.status')}>
                {t(`status.${user.status}` as const)}
              </Row>
              <Row label={t('profile.field.memberSince')}>
                {formatDateTime(user.createdAt, locale)}
              </Row>
              <Row label={t('profile.field.lastLogin')}>
                {formatDateTime(user.lastLoginAt, locale)}
              </Row>
              <Row label={t('profile.field.accountId')}>
                <span className="break-all font-mono text-xs">{user.id}</span>
              </Row>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
